import React, { Component, useEffect } from 'react';
import { Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { log } from '../utils/jsUtils';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../reduxStore/modules/user/actions';
import { selectIsLoggedIn } from '../reduxStore/modules/user/selectors';
import { selectIsAuthStateLoading } from '../reduxStore/modules/common/selectors';
import { setIsAppLoading } from '../reduxStore/modules/common/actions';
import AppLoadingSpinner from './SharedComponents/AppLoadingSpinner';
import MemberDrawer from './MemberDrawer';
import HeaderTopBar from './HeaderTopBar';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import memberHeaderStyles from './Feed/Styles/MemberHeader.Style';
import { useI18n } from '../contexts/I18nContext';

type Props = {
  forceLogout: boolean,
  exact: boolean,
  path: string
};

// Route-to-title mapping for loading state
const ROUTE_TITLES = {
  '/member': 'Feed',
  '/customers': 'Customers',
  '/staff': 'Staff',
  '/neo': 'Neo the bot',
  '/tags': 'Tags',
  '/integrations': 'Integrations',
  '/billing': 'Billing',
  '/share': 'Share Neo'
};

const PrivateRoute = (props: Props) => {
  const {
    forceLogout,
    exact,
    path,
    children
  } = props;

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const i18n = useI18n();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isAuthLoading = useSelector(selectIsAuthStateLoading);
  
  // Handle force logout via useEffect
  useEffect(() => {
    if (forceLogout) {
      const performLogout = async () => {
        log('[PrivateRoute] Force logout initiated');
        
        try {
          const result = await dispatch(logout());
          log('[PrivateRoute] Logout completed:', result);
          
          // Redirect to home page after logout
          history.push('/');
        } catch (error) {
          log('[PrivateRoute] Error during logout:', error);
          history.push('/');
        }
      };
      
      performLogout();
    }
  }, [forceLogout, dispatch, history]);
  
  // Set app-level loading state based on auth loading
  useEffect(() => {
    // Only set app loading if authentication is still being determined
    if (isAuthLoading) {
      dispatch(setIsAppLoading(true));
    } else {
      dispatch(setIsAppLoading(false));
    }
    
    // Clean up when unmounting
    return () => {
      if (isAuthLoading) {
        dispatch(setIsAppLoading(false));
      }
    };
  }, [isAuthLoading, dispatch]);
  
  // Don't render anything during forced logout - wait for redirect
  if (forceLogout) {
    return null;
  }
  
  // If auth state is loading, show app shell with loading indicator
  if (isAuthLoading) {
    log('[PrivateRoute] Auth state is still loading, showing app shell with drawer and header');

    // Resolve dynamic page title based on current route
    const titleKey = ROUTE_TITLES[location.pathname] || 'Loading...';
    const pageTitle = i18n.t(titleKey);

    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        {/* Render actual MemberDrawer in loading state */}
        <MemberDrawer
          mobileOpen={false}
          usehandleDrawerToggle={() => {}}
          isLoading={true}
        />

        {/* Main content area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header with title and loading state */}
          <AppBar position="static" sx={{ height: 140 }}>
            <Toolbar disableGutters>
              <Grid container direction="column">
                {/* Use HeaderTopBar with loading state */}
                <HeaderTopBar
                  usehandleDrawerToggle={() => {}}
                  isLoading={true}
                >
                  <Typography
                    variant="h2"
                    sx={(theme) => memberHeaderStyles.pageTitle(theme)}
                  >
                    {pageTitle}
                  </Typography>
                </HeaderTopBar>

                {/* Bottom bar placeholder */}
                <Grid
                  item
                  sx={(theme) => ({
                    ...memberHeaderStyles.rowSub(theme),
                    ...memberHeaderStyles.bottomBar(theme)
                  })}
                >
                  <Box sx={{ flexGrow: 1 }} />
                </Grid>

                {/* AppLoadingSpinner */}
                <AppLoadingSpinner />
              </Grid>
            </Toolbar>
          </AppBar>

          {/* Content area - empty while loading */}
          <Box sx={{ flexGrow: 1 }} />
        </Box>
      </Box>
    );
  }
  
  log('[PrivateRoute] isLoggedIn:', isLoggedIn, 'isAuthLoading:', isAuthLoading);
  
  if (isLoggedIn) {
    return (
      <Route
        exact={exact}
        path={path} 
      >
        {children}
      </Route>
    );
  }
  
  log('[PrivateRoute] redirect to /login... location: ', location);
  return (
    <Redirect
      to={{
        pathname: '/login',
        state: { from: location }
      }}
    />
  );
};

export default PrivateRoute;
